import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  filterMessages,
  type MessageBucket,
  type MessagesRequest,
  type PlayerMessage,
} from '@/domain/messages';
import { getUserSettings } from './auth';
import { api } from './client';
import { endpoints } from './endpoints';
import { queryKeys } from './query-keys';

/** Profile inbox list (`sb/api/player/messages/list`). */
export function listMessages(
  request: MessagesRequest,
  signal?: AbortSignal,
): Promise<PlayerMessage[]> {
  return api.post<PlayerMessage[]>(
    endpoints.messages.list.host,
    endpoints.messages.list.path,
    request as unknown as Record<string, unknown>,
    signal,
  );
}

/** Profile message details (`sb/api/player/messages/details`) — marks unread as read server-side. */
export function getMessageDetails(
  request: MessagesRequest,
  signal?: AbortSignal,
): Promise<PlayerMessage> {
  return api.post<PlayerMessage>(
    endpoints.messages.details.host,
    endpoints.messages.details.path,
    request as unknown as Record<string, unknown>,
    signal,
  );
}

export interface UseMessagesResult {
  bucket: MessageBucket;
  setBucket: (bucket: MessageBucket) => void;
  rows: PlayerMessage[];
  allCount: number;
  newCount: number;
  readCount: number;
  isLoading: boolean;
  listError: Error | null;
  machineGuid: string;
}

/**
 * Messages list: fetch once, filter All/New/Read client-side (Figma chips).
 */
export function useMessages(enabled = true): UseMessagesResult {
  const [bucket, setBucket] = useState<MessageBucket>('all');

  const user = getUserSettings();
  const machineGuid = user?.Guid?.trim() ?? '';

  const listQuery = useQuery({
    queryKey: queryKeys.player.messages({ machineGuid }),
    enabled: enabled && Boolean(machineGuid),
    queryFn: async ({ signal }) => {
      const response = await listMessages({ Value: machineGuid }, signal);
      return Array.isArray(response) ? response : [];
    },
  });

  const allRows = listQuery.data ?? [];

  const allCount = allRows.length;
  const newCount = useMemo(() => filterMessages(allRows, 'new').length, [allRows]);
  const readCount = useMemo(() => filterMessages(allRows, 'read').length, [allRows]);

  const rows = useMemo(() => filterMessages(allRows, bucket), [allRows, bucket]);

  return {
    bucket,
    setBucket,
    rows,
    allCount,
    newCount,
    readCount,
    isLoading: listQuery.isLoading,
    listError: listQuery.error instanceof Error ? listQuery.error : null,
    machineGuid,
  };
}

export interface UseMessageDetailsResult {
  details: PlayerMessage | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Message details. Opening an unread message marks it read on the server;
 * we mirror that into the list cache when the response reports prior unread state.
 */
export function useMessageDetails(
  messageId: string | null,
  enabled = true,
): UseMessageDetailsResult {
  const queryClient = useQueryClient();
  const user = getUserSettings();
  const machineGuid = user?.Guid?.trim() ?? '';

  const detailsQuery = useQuery({
    queryKey: queryKeys.player.messageDetail(messageId),
    enabled: enabled && Boolean(messageId),
    queryFn: async ({ signal }) => {
      return getMessageDetails({ Value: messageId! }, signal);
    },
  });

  useEffect(() => {
    const details = detailsQuery.data;
    if (!details || !messageId || !machineGuid) return;
    // API returns previous read state: false means this open marked it read.
    if (details.ReadTimeStamp !== false) return;

    queryClient.setQueryData<PlayerMessage[]>(
      queryKeys.player.messages({ machineGuid }),
      (prev) => {
        if (!prev) return prev;
        return prev.map((row) =>
          row.Id === messageId ? { ...row, ReadTimeStamp: true } : row,
        );
      },
    );
  }, [detailsQuery.data, machineGuid, messageId, queryClient]);

  return {
    details: detailsQuery.data ?? null,
    isLoading: detailsQuery.isLoading,
    error: detailsQuery.error instanceof Error ? detailsQuery.error : null,
  };
}
